/* eslint-disable import/first */
// SENTRY MUST BE FIRST - Initialize before anything else to capture early errors
import './SentryInit';

import { ANDROID_VERSION, IOS_VERSION, isProd } from './constants/constants';

// eslint-disable-next-line import/order
/* eslint-disable import/order */
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ConnectedRouter } from 'connected-react-router';
import { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import semverGt from 'semver/functions/gt';

import { CookiesProvider } from 'react-cookie';
import hardSet from 'redux-persist/lib/stateReconciler/hardSet';
import storage from 'redux-persist/lib/storage';
import 'sanitize.css/sanitize.css';
import Analytics from './Analytics';
import { CenterLoader } from './components/loader';
import BiometricProvider from './hooks/componentHooks/useBiometrics';
import OAuthLoginProvider from './hooks/useOAuthLogin';
import { GlobalStyles } from './index_styles';
import store, { history, persistor } from './store';
import './styles/scss/index.scss';
import DeltaThemeProvider from './ThemeProvider';
import postMsgToRN from './utils/postToRn';
import crossBrowserListener from './utils/redux-persist';

import '@delta-exchange/design-system/web';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import App from 'containers/app';
import AutoMobileOTPVerificationProvider from 'hooks/useAutoMobileOTPVerification';
import { growthBookInstance } from './growthbook';
import './i18n/config';
import './styles/root.scss';

const persistConfig = {
  key: 'root',
  storage,
  stateReconciler: hardSet,
};
window.addEventListener('storage', crossBrowserListener(store, persistConfig));

const DeltaApp = () => {
  return (
    <CookiesProvider>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ConnectedRouter history={history}>
            <BiometricProvider>
              <OAuthLoginProvider>
                <AutoMobileOTPVerificationProvider>
                  <DeltaThemeProvider>
                    <>
                      <GlobalStyles />
                      <GrowthBookProvider growthbook={growthBookInstance}>
                        <Suspense fallback={<CenterLoader />}>
                          <GoogleOAuthProvider
                            clientId={process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID!}
                          >
                            <App />
                          </GoogleOAuthProvider>
                        </Suspense>
                      </GrowthBookProvider>
                      <Analytics />
                    </>
                  </DeltaThemeProvider>
                </AutoMobileOTPVerificationProvider>
              </OAuthLoginProvider>
            </BiometricProvider>
          </ConnectedRouter>
        </PersistGate>
      </Provider>
    </CookiesProvider>
  );
};

if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line import/no-extraneous-dependencies
  import('disable-react-error-overlay').then(() => {
    const iframe = document.querySelector('iframe');
    if (iframe) iframe.remove();
  });
}

const IS_SHOW_APP_UPDATE_POPUP = semverGt(
  ANDROID_VERSION,
  // eslint-disable-next-line no-underscore-dangle
  window?._RNAppVersion || '0.0.0'
);

if (IS_SHOW_APP_UPDATE_POPUP)
  postMsgToRN('VERSION', {
    android: ANDROID_VERSION,
    ios: IOS_VERSION,
    title: 'App Update Available',
    body: 'Update your app for an improved experience',
    positiveButtonText: 'Update',
    NegativeButtonText: 'No, thanks',
    mandatoryUpdate: false,
  });

const target = document.querySelector('#root')!;
const root = createRoot(target);
root.render(<DeltaApp />);

if (isProd) {
  import('./registerServiceWorker').then(module => module.default());
}
